// src/services/revenueAnalytics.js

import api from '../api';

const REVENUE_SESSION_KEY = 'barterfi_revenue_session_id';

const PROFILE_BANNERS_EXPERIMENT = 'profile_banners';

const BANNER_SCREEN_VIEW = 'banner_screen_view';
const BANNER_PREVIEW_OPEN = 'banner_preview_open';

/**
 * Detect which BarterFi network the frontend is currently using.
 *
 * Testnet:
 * https://testnet.barterfi.app
 *
 * Everything else defaults to mainnet.
 */
const getCurrentNetwork = () => {
  try {
    const hostname = String(window.location.hostname || '')
      .trim()
      .toLowerCase();

    if (hostname.includes('testnet')) {
      return 'testnet';
    }
  } catch (_) {}

  return 'mainnet';
};

/**
 * Generates a lightweight ID for the current browser session.
 */
const createSessionId = () => {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
  } catch (_) {}

  return [
    Date.now(),
    Math.random().toString(36).slice(2),
    Math.random().toString(36).slice(2),
  ].join('-');
};

/**
 * Keeps the same analytics session ID until the browser tab closes.
 */
const getRevenueSessionId = () => {
  try {
    const existingSessionId = sessionStorage.getItem(
      REVENUE_SESSION_KEY
    );

    if (existingSessionId) {
      return existingSessionId;
    }

    const newSessionId = createSessionId();

    sessionStorage.setItem(
      REVENUE_SESSION_KEY,
      newSessionId
    );

    return newSessionId;
  } catch (_) {
    return createSessionId();
  }
};

/**
 * Generic revenue experiment tracker.
 *
 * Analytics must never interrupt the real user experience.
 * Errors are caught and returned instead of being thrown.
 */
const trackRevenueEvent = async ({
  experimentKey,
  eventType,
  itemId = null,
  network,
} = {}) => {
  if (!experimentKey || !eventType) {
    return {
      tracked: false,
      ignored: true,
      reason: 'missing_event_details',
    };
  }

  const effectiveNetwork =
    network === 'testnet' || network === 'mainnet'
      ? network
      : getCurrentNetwork();

  try {
    const response = await api.post(
      '/revenue-analytics/events',
      {
        experimentKey,
        eventType,
        itemId: itemId || null,
        sessionId: getRevenueSessionId(),
      },
      {
        headers: {
          'X-Network': effectiveNetwork,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.warn(
      'Revenue analytics event was not recorded:',
      {
        experimentKey,
        eventType,
        status: error?.response?.status,
        message:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message,
      }
    );

    return {
      tracked: false,
      error: true,
    };
  }
};

/**
 * Records that a user opened the Profile Banners collection screen.
 *
 * The backend applies the 30-minute deduplication rule.
 */
const trackBannerScreenView = ({
  network,
} = {}) => {
  return trackRevenueEvent({
    experimentKey: PROFILE_BANNERS_EXPERIMENT,
    eventType: BANNER_SCREEN_VIEW,
    network,
  });
};

/**
 * Records that a user opened the preview drawer for a banner.
 *
 * The backend validates the banner and obtains its trusted:
 * - name
 * - slug
 * - collection
 * - price
 */
const trackBannerPreviewOpen = (
  banner,
  {
    network,
  } = {}
) => {
  const bannerId =
    banner?._id ||
    banner?.id ||
    banner?.bannerId ||
    '';

  if (!bannerId) {
    return Promise.resolve({
      tracked: false,
      ignored: true,
      reason: 'missing_banner_id',
    });
  }

  return trackRevenueEvent({
    experimentKey: PROFILE_BANNERS_EXPERIMENT,
    eventType: BANNER_PREVIEW_OPEN,
    itemId: bannerId,
    network,
  });
};

export {
  getCurrentNetwork,
  getRevenueSessionId,
  trackRevenueEvent,
  trackBannerScreenView,
  trackBannerPreviewOpen,
};

export default {
  trackRevenueEvent,
  trackBannerScreenView,
  trackBannerPreviewOpen,
};
